import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, User, Mail, Phone, Camera, KeyRound, Loader2 } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TopBar } from "@/components/layout/TopBar";

const MAX_PICTURE_BYTES = 400 * 1024; // 400 KB — keeps DB rows small; enough for a 512px avatar

export default function AccountSettings() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const forced = params.get("force") === "1" || (user && user.must_change_password);

  const [profile, setProfile] = useState({
    name: "", first_name: "", last_name: "",
    email: "", phone: "", picture: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    setProfile({
      name: user.name || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone: user.phone || "",
      picture: user.picture || "",
    });
  }, [user]);

  const dirty = useMemo(() => {
    if (!user) return false;
    return (
      (profile.name || "") !== (user.name || "") ||
      (profile.first_name || "") !== (user.first_name || "") ||
      (profile.last_name || "") !== (user.last_name || "") ||
      (profile.email || "").toLowerCase() !== (user.email || "").toLowerCase() ||
      (profile.phone || "") !== (user.phone || "") ||
      (profile.picture || "") !== (user.picture || "")
    );
  }, [profile, user]);

  const onPickPicture = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Sélectionnez un fichier image.");
      return;
    }
    if (f.size > MAX_PICTURE_BYTES) {
      toast.error("Image trop volumineuse (max 400 Ko).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setProfile((p) => ({ ...p, picture: String(reader.result || "") }));
    };
    reader.readAsDataURL(f);
  };

  const saveProfile = async () => {
    if (!dirty) return;
    setSavingProfile(true);
    try {
      const payload = {
        name: profile.name || null,
        first_name: profile.first_name || null,
        last_name: profile.last_name || null,
        phone: profile.phone || null,
        picture: profile.picture || null,
      };
      const emailChanged =
        (profile.email || "").toLowerCase() !== (user.email || "").toLowerCase();
      if (emailChanged) payload.email = profile.email;

      const { data } = await api.patch("/auth/me/profile", payload);
      if (data.access_token) setToken(data.access_token);
      await refreshUser();
      toast.success(
        emailChanged
          ? "Profil mis à jour. Un nouveau courriel de vérification vous a été envoyé."
          : "Profil mis à jour."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer le profil.");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (pwd.next !== pwd.confirm) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas.");
      return;
    }
    if (pwd.next.length < 8) {
      toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setSavingPwd(true);
    try {
      const { data } = await api.post("/auth/me/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      if (data.access_token) setToken(data.access_token);
      await refreshUser();
      setPwd({ current: "", next: "", confirm: "" });
      toast.success("Mot de passe modifié. Les sessions précédentes ont été révoquées.");
      // Drop ?force=1 so the yellow banner disappears immediately without reloading.
      if (params.get("force")) navigate("/app/account", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de changer le mot de passe.");
    } finally {
      setSavingPwd(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="account-settings-root">
      <TopBar
        title="Mon compte"
        subtitle="Vos informations personnelles et votre sécurité"
      />
      <div className="px-6 md:px-10 py-10 md:py-14 max-w-5xl mx-auto space-y-8">
        {forced && (
          <div
            data-testid="account-force-change-banner"
            className="rounded-md border border-[#DCC29A] bg-[#FDF7EC] px-5 py-4 flex items-start gap-3"
          >
            <ShieldAlert className="h-5 w-5 text-[#8A5A00] mt-0.5 shrink-0" strokeWidth={1.6} />
            <div className="text-sm text-[#3A2A00]">
              <div className="font-medium">Changement de mot de passe requis</div>
              <div className="text-xs mt-1 text-[#5C4300]">
                Vous êtes connecté avec un mot de passe temporaire (généré lors du
                premier démarrage). Choisissez votre mot de passe personnel ci-dessous
                pour continuer.
              </div>
            </div>
          </div>
        )}

        {/* -------- Identity card -------- */}
        <section
          className="rounded-md border border-[#E4E1D8] bg-white p-6 md:p-8"
          data-testid="account-identity-card"
        >
          <div className="flex items-center gap-2 mb-6">
            <User className="h-4 w-4 text-[#0B2545]" strokeWidth={1.6} />
            <h2 className="text-base font-medium text-[#1F2937]">Identité</h2>
          </div>

          <div className="flex items-start gap-6 mb-6">
            <div className="shrink-0">
              <div className="h-24 w-24 rounded-full bg-[#F2EFE7] overflow-hidden border border-[#E4E1D8]">
                {profile.picture ? (
                  <img
                    src={profile.picture}
                    alt="avatar"
                    className="h-full w-full object-cover"
                    data-testid="account-picture-preview"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-[#8B8B8F] font-mono text-xs uppercase">
                    {(profile.first_name?.[0] || profile.email?.[0] || "?")}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#0B2545] hover:underline"
                onClick={() => fileRef.current?.click()}
                data-testid="account-picture-btn"
              >
                <Camera className="h-3.5 w-3.5" strokeWidth={1.6} />
                Changer la photo
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickPicture}
                data-testid="account-picture-file"
              />
              {profile.picture && (
                <button
                  type="button"
                  onClick={() => setProfile((p) => ({ ...p, picture: "" }))}
                  className="mt-1 w-full text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] hover:text-[#5B5B62]"
                  data-testid="account-picture-remove"
                >
                  Retirer
                </button>
              )}
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nom complet (affiché)">
                <input
                  data-testid="account-name-input"
                  value={profile.name}
                  onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                  className={inputCls}
                />
              </Field>
              <Field label="Nom d'utilisateur (privé)">
                <input
                  value={user.username || ""}
                  disabled
                  className={`${inputCls} opacity-60`}
                />
              </Field>
              <Field label="Prénom">
                <input
                  data-testid="account-firstname-input"
                  value={profile.first_name}
                  onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
                  className={inputCls}
                />
              </Field>
              <Field label="Nom de famille">
                <input
                  data-testid="account-lastname-input"
                  value={profile.last_name}
                  onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
                  className={inputCls}
                />
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Adresse courriel" icon={<Mail className="h-3.5 w-3.5" strokeWidth={1.6} />}>
              <input
                data-testid="account-email-input"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                className={inputCls}
              />
              {(profile.email || "").toLowerCase() !== (user.email || "").toLowerCase() && (
                <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#8A5A00]">
                  Changer l&apos;adresse déconnecte les sessions actives
                </div>
              )}
            </Field>
            <Field label="Téléphone" icon={<Phone className="h-3.5 w-3.5" strokeWidth={1.6} />}>
              <input
                data-testid="account-phone-input"
                type="tel"
                placeholder="+41 79 000 00 00"
                value={profile.phone}
                onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              data-testid="account-save-profile"
              onClick={saveProfile}
              disabled={savingProfile || !dirty}
              className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-6 py-2.5 transition-colors"
            >
              {savingProfile && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />}
              Enregistrer les changements
            </button>
          </div>
        </section>

        {/* -------- Password card -------- */}
        <section
          className="rounded-md border border-[#E4E1D8] bg-white p-6 md:p-8"
          data-testid="account-password-card"
        >
          <div className="flex items-center gap-2 mb-6">
            <KeyRound className="h-4 w-4 text-[#0B2545]" strokeWidth={1.6} />
            <h2 className="text-base font-medium text-[#1F2937]">
              {forced ? "Choisir mon mot de passe" : "Changer mon mot de passe"}
            </h2>
          </div>
          <p className="text-xs text-[#5B5B62] mb-5 leading-relaxed max-w-2xl">
            Toutes vos sessions actives (navigateurs, appareils) seront révoquées après
            le changement. Vous serez automatiquement reconnecté sur cet onglet.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={forced ? "Mot de passe temporaire" : "Mot de passe actuel"}>
              <input
                data-testid="account-pwd-current"
                type="password"
                autoComplete="current-password"
                value={pwd.current}
                onChange={(e) => setPwd((s) => ({ ...s, current: e.target.value }))}
                className={inputCls}
              />
            </Field>
            <Field label="Nouveau mot de passe">
              <input
                data-testid="account-pwd-next"
                type="password"
                autoComplete="new-password"
                value={pwd.next}
                onChange={(e) => setPwd((s) => ({ ...s, next: e.target.value }))}
                className={inputCls}
              />
            </Field>
            <Field label="Confirmer le nouveau">
              <input
                data-testid="account-pwd-confirm"
                type="password"
                autoComplete="new-password"
                value={pwd.confirm}
                onChange={(e) => setPwd((s) => ({ ...s, confirm: e.target.value }))}
                className={inputCls}
              />
            </Field>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              data-testid="account-save-password"
              onClick={changePassword}
              disabled={
                savingPwd ||
                !pwd.current ||
                !pwd.next ||
                pwd.next.length < 8 ||
                pwd.next !== pwd.confirm
              }
              className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-6 py-2.5 transition-colors"
            >
              {savingPwd && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />}
              Mettre à jour le mot de passe
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2 text-sm text-[#1F2937]";

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">
        {icon}
        <span>{label}</span>
      </div>
      {children}
    </label>
  );
}
